// Table views (migration 045; Mitch: "per-user"): each person's saved views of a table and their working layout of it,
// the same on any computer they sign in on. DataTable uses them for every table that has a name (`exportName`).
// - A layout is the columns in order (by key), the ones hidden, and widths dragged (px); with a sort.
// - A view is a named layout and sort plus the page's filters and tab (`search`, shared/table-views.ts): what's open (a
//   record, the side pane) is never part of one.
// - The working layout (table_pref) is what the table shows now and the view picked last; it changes as columns are
//   dragged, hidden or sorted, and a view is "changed" while the table differs from it (Save, Save as new view, Discard).
// Where they're kept is a backend: staff ones are synced records written by mutations; the vendor portal gives its own
// (web/src/portal/views.ts, its people's views through /v/api/views) with ViewsBackendContext.
import { createContext, useContext } from "react";
import type { TablePref, TableView } from "../../../shared/db.gen.ts";
import { type Layout, type Sort, savableLayout } from "../../../shared/table-views.ts";
import { mutate } from "./mutate.ts";
import { me } from "./session.ts";
import { useRows } from "./sync.ts";

export type { Layout, Sort } from "../../../shared/table-views.ts";
export { viewSearch, withViewSearch } from "../../../shared/table-views.ts";

export type PrefPatch = Partial<Pick<TablePref, "view_id">> & { layout?: Layout; sort?: Sort | null };
export type NewView = { id: string; table_key: string; name: string; layout: Layout; sort: Sort | null; search: string; position: number };
export type ViewPatch = Partial<Omit<NewView, "id" | "table_key">>;

/** Where one person's views and working layouts are kept. `useAll` is a hook: their views and layouts of every table. */
export type ViewsBackend = {
  useAll: () => { owner: string | null; views: TableView[]; prefs: TablePref[] };
  savePref: (key: string, existing: TablePref | undefined, patch: PrefPatch) => void;
  createView: (view: NewView) => Promise<boolean>;
  updateView: (id: string, patch: ViewPatch) => Promise<boolean>;
  removeView: (id: string) => void;
};

/** Staff: synced table_view and table_pref rows, the signed-in person's own. */
const staffViews: ViewsBackend = {
  useAll: () => {
    const user = me();
    const views = useRows("table_view");
    const prefs = useRows("table_pref");
    // A row made in this browser has no user_id until the server's copy comes back (the server sets it): it's this
    // person's, so the first resize, or a view saved and picked, shows at once instead of snapping back meanwhile.
    const mine = (r: { user_id?: string | null }) => !r.user_id || r.user_id === user?.id;
    return user ? { owner: user.id, views: views.filter(mine), prefs: prefs.filter(mine) } : { owner: null, views: [], prefs: [] };
  },
  savePref: (key, pref, patch) => {
    if (pref) void mutate("table_pref.update", { id: pref.id, patch: patch as never });
    else void mutate("table_pref.create", { id: crypto.randomUUID(), table_key: key, view_id: null, layout: {}, sort: null, ...patch } as never);
  },
  createView: (view) => mutate("table_view.create", view as never),
  updateView: (id, patch) => mutate("table_view.update", { id, patch: patch as never }),
  removeView: (id) => void mutate("table_view.remove", { id }),
};

export const ViewsBackendContext = createContext<ViewsBackend>(staffViews);

/** Every save's layout as the server keeps it (widths in range), however the table made it. */
const savable = <T extends { layout?: Layout }>(x: T): T => (x.layout ? { ...x, layout: savableLayout(x.layout) } : x);

/** The person's views of a table (`key`, as DataTable names it) and their working layout; nothing without a key. */
export function useTableViews(key: string | undefined) {
  const backend = useContext(ViewsBackendContext);
  const all = backend.useAll();
  const mine = !!key && !!all.owner;
  const views = mine ? all.views.filter((v) => v.table_key === key).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)) : [];
  const pref = mine ? all.prefs.find((p) => p.table_key === key) : undefined;

  /** The working layout: changes what the table shows now (made the first time). */
  const savePref = (patch: PrefPatch) => {
    if (key && all.owner) backend.savePref(key, pref, savable(patch));
  };
  return {
    enabled: mine,
    views,
    pref,
    savePref,
    createView: (view: NewView) => backend.createView(savable(view)),
    updateView: (id: string, patch: ViewPatch) => backend.updateView(id, savable(patch)),
    removeView: backend.removeView,
  };
}

export const layoutOf = (row: Pick<TableView, "layout"> | Pick<TablePref, "layout"> | undefined): Layout => (row?.layout ?? {}) as Layout;
export const sortOf = (row: Pick<TableView, "sort"> | Pick<TablePref, "sort"> | undefined): Sort | null => (row?.sort ?? null) as Sort | null;
